import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Accesso effettuato");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy to-[#0a1530]" />
        <div className="relative flex items-center">
          <div className="inline-flex bg-white px-4 py-3 rounded-sm">
            <img src="/logo-mp.png" alt="Martegiani Petroli Srl" className="h-10 w-auto" />
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-white text-5xl leading-[0.95] tracking-tight">
            Area<br />Riservata
          </h1>
          <p className="text-white/50 mt-6 max-w-sm">
            Gestisci le richieste di preventivo ricevute dal sito. Accesso esclusivo
            all'amministrazione.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[var(--bg)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-orange transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <h2 className="font-display text-navy text-3xl">Accedi</h2>
          <p className="text-ink-soft mt-2 mb-8">Inserisci le tue credenziali.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-soft">Utente</Label>
              <Input type="text" required value={username} onChange={(e) => setUsername(e.target.value)} data-testid="login-username" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-soft">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-navy text-white h-12 flex items-center justify-center gap-2 font-semibold uppercase tracking-[0.12em] text-sm hover:bg-orange transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Accedi
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
